// src/components/Widgets/PackagingWidget.jsx
import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { navigateTo, ROUTES } from "../../lib/nav";
import { usePackaging } from "../../contexts/PackagingContext";
import WidgetHeader from "../Common/WidgetHeader";
import PackagingItemModal from "../Packaging/PackagingItemModal";

export default function PackagingWidget({ backTo = "/dashboard" }) {
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  // Context may still be initializing; coerce everything to safe fallbacks
  const pkg = usePackaging?.() || {};
  const items = Array.isArray(pkg.items) ? pkg.items : [];
  const rules = Array.isArray(pkg.rules) ? pkg.rules : [];

  // Derived (with safe fallbacks in case provider hasn’t populated them yet)
  const totalItems = Number.isFinite(Number(pkg.totalItems)) ? Number(pkg.totalItems) : items.length;
  const activeRulesCount = Number.isFinite(Number(pkg.activeRulesCount))
    ? Number(pkg.activeRulesCount)
    : rules.filter((r) => r?.active).length;

  const byCategory = (Array.isArray(pkg.byCategory) ? pkg.byCategory : []).map((c) => ({
    name: String(c?.name ?? "Uncategorized"),
    count: Number(c?.count ?? 0),
  }));

  const lastUpdated = Number(pkg.lastUpdated ?? 0);
  const fmtDate = (ts) => (ts ? new Date(ts).toLocaleString() : "—");

  const getStock = (it) => {
    const stock = it?.stock ?? it?.stockQty ?? it?.quantity ?? null;
    const n = Number(stock);
    return Number.isFinite(n) ? n : null;
  };
  const isLow = (it) => {
    const limit = it?.lowStockLimit;
    const stock = getStock(it);
    if (limit == null || stock == null) return false;
    return stock <= Number(limit);
  };
  const isOut = (it) => {
    const stock = getStock(it);
    if (stock == null) return false;
    return stock <= 0;
  };

  const perUnitBase = (it) => {
    const mode = it?.pricingMode || "unit";
    if (mode === "unit") return Number(it?.unitPrice ?? 0) || 0;
    const packPrice = Number(it?.packPrice ?? 0) || 0;
    const unitsPerPack = Math.max(1, Number(it?.unitsPerPack ?? 1) || 1);
    return packPrice / unitsPerPack;
  };

  const money = (v, currency = "AUD", locale = "en-AU") => {
    const n = Number(v || 0);
    try {
      return new Intl.NumberFormat(locale, { style: "currency", currency }).format(n);
    } catch {
      return `${currency} ${n.toFixed(2)}`;
    }
  };

  const recent = useMemo(() => {
    const arr = Array.isArray(items) ? [...items] : [];
    arr.sort((a, b) => {
      // IMPORTANT first: out-of-stock, then low-stock, then recently updated
      const aOut = isOut(a) ? 1 : 0;
      const bOut = isOut(b) ? 1 : 0;
      if (aOut !== bOut) return bOut - aOut;

      const aLow = isLow(a) ? 1 : 0;
      const bLow = isLow(b) ? 1 : 0;
      if (aLow !== bLow) return bLow - aLow;

      const aTs = Number(a?.updatedAt || a?.createdAt || 0) || 0;
      const bTs = Number(b?.updatedAt || b?.createdAt || 0) || 0;
      return bTs - aTs;
    });
    return arr.slice(0, 4);
  }, [items]);

  const renderThumb = useCallback((image) => {
    if (!image) {
      return <div className="h-12 w-12 rounded bg-black/10 dark:bg-white/10 flex-shrink-0" />;
    }
    if (typeof image === "string") {
      return (
        <img
          src={image}
          alt=""
          className="h-12 w-12 object-cover rounded border border-black/10 dark:border-white/10 flex-shrink-0"
          onError={(e) => (e.currentTarget.style.display = "none")}
          loading="lazy"
        />
      );
    }
    const url = URL.createObjectURL(image);
    return (
      <img
        src={url}
        alt=""
        className="h-12 w-12 object-cover rounded border border-black/10 dark:border-white/10 flex-shrink-0"
        onLoad={(e) => URL.revokeObjectURL(e.currentTarget.src)}
        onError={(e) => {
          URL.revokeObjectURL(e.currentTarget.src);
          e.currentTarget.style.display = "none";
        }}
      />
    );
  }, []);

  const onAddNew = () => {
    setEditing(null);
    setOpen(true);
  };

  const onSelect = (it) => {
    setEditing(it);
    setOpen(true);
  };

  return (
    <div className="rounded-xl p-4 h-full flex flex-col bg-white/60 border border-black/10 backdrop-blur-md text-black shadow
                    dark:bg-white/5 dark:border-white/10 dark:text-white">
      <WidgetHeader
        title={<span className="font-semibold" style={{ color: "var(--color-primary)" }}>Packaging</span>}
        help={
          "Manage packaging supplies and packaging rules. The preview highlights items that may need attention; “Rules” controls auto-assignment."
        }
        to={backTo}
        right={
          <div className="flex gap-2">
            <button
              onClick={onAddNew}
              className="px-3 py-1 rounded text-white text-sm transition-colors hover:opacity-90"
              style={{ backgroundColor: "var(--color-primary)" }}
            >
              + New
            </button>
            <button
              onClick={() => navigateTo(navigate, ROUTES.PACKAGING_LIBRARY, { source: "PackagingWidget:viewAll" })}
              className="px-3 py-1 rounded border text-sm border-black/10 dark:border-white/10"
            >
              View All
            </button>
            <button
              onClick={() => navigateTo(navigate, ROUTES.PACKAGING_RULES, { source: "PackagingWidget:rules" })}
              className="px-3 py-1 rounded border text-sm border-black/10 dark:border-white/10"
            >
              Rules
            </button>
          </div>
        }
      />

      <div className="mt-2 grid grid-cols-2 gap-2">
        <div className="rounded-lg border border-black/10 dark:border-white/10 bg-white/70 dark:bg-white/5 p-2">
          <div className="text-xs text-black/60 dark:text-white/60">Total items</div>
          <div className="text-sm font-semibold tabular-nums">{totalItems}</div>
        </div>
        <div className="rounded-lg border border-black/10 dark:border-white/10 bg-white/70 dark:bg-white/5 p-2">
          <div className="text-xs text-black/60 dark:text-white/60">Active rules</div>
          <div className="text-sm font-semibold tabular-nums">{activeRulesCount}</div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto space-y-3 mt-2">
        {recent.length > 0 ? (
          <>
            {recent.map((it) => {
              const unit = perUnitBase(it);
              return (
                <button
                  key={it.id || it.name}
                  onClick={() => onSelect(it)}
                  className="w-full text-left rounded-lg p-3 border border-black/10 dark:border-white/10
                             bg-white/70 dark:bg-white/5 shadow-sm hover:shadow transition
                             flex items-center justify-between"
                  title="Edit packaging item"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {renderThumb(it.image)}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-sm truncate" title={it.name || "Untitled"}>
                          {it.name || "Untitled"}
                        </h4>
                        {isOut(it) && (
                          <span
                            className="inline-block text-[10px] leading-none px-2 py-1 rounded-full
                                       border border-red-200 bg-red-50 text-red-700
                                       dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300"
                            title="Out of stock"
                          >
                            Out
                          </span>
                        )}
                        {!isOut(it) && isLow(it) && (
                          <span
                            className="inline-block text-[10px] leading-none px-2 py-1 rounded-full
                                       border border-amber-200 bg-amber-50 text-amber-800
                                       dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200"
                            title="Low stock"
                          >
                            Low
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-black/60 dark:text-white/60 truncate">
                        SKU: {it.sku || "N/A"} — {it.category || "—"}
                      </p>
                      <p className="text-xs text-black/50 dark:text-white/50 truncate">
                        {getStock(it) == null ? "Stock: —" : `Stock: ${getStock(it)} ${it.uom || "each"}`}
                      </p>
                    </div>
                  </div>

                  <div className="text-right text-sm flex-shrink-0">
                    <p className="text-black dark:text-white tabular-nums">{money(unit)}</p>
                    <p className="text-xs text-black/50 dark:text-white/50">per {it.uom || "unit"}</p>
                  </div>
                </button>
              );
            })}

            {/* Ensure at least 4 rows (empty state placeholders) */}
            {Array.from({ length: Math.max(0, 4 - recent.length) }).map((_, idx) => (
              <div
                key={`placeholder-${idx}`}
                className="w-full rounded-lg p-3 border border-black/10 dark:border-white/10
                           bg-white/50 dark:bg-white/5 flex items-center justify-between opacity-60"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-12 w-12 rounded bg-black/10 dark:bg-white/10 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="h-3 w-40 rounded bg-black/10 dark:bg-white/10" />
                    <div className="h-3 w-28 rounded bg-black/10 dark:bg-white/10 mt-2" />
                  </div>
                </div>
                <div className="h-3 w-16 rounded bg-black/10 dark:bg-white/10" />
              </div>
            ))}
          </>
        ) : (
          <div className="rounded-lg border border-black/10 dark:border-white/10 bg-white/70 dark:bg-white/5 p-4">
            <p className="text-sm text-black/70 dark:text-white/70">
              No packaging items yet. Click <span className="font-semibold">+ New</span> to add your first packaging item.
            </p>
          </div>
        )}
      </div>

      <div className="text-[11px] text-black/50 dark:text-white/50 mt-2">
        Last update: {fmtDate(lastUpdated)}
      </div>

      {open && (
        <PackagingItemModal
          open
          initialItem={editing}
          onClose={() => {
            setOpen(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}
